<template>
    <v-row no-gutters class="d-flex justify-center w-full" style="overflow-y: auto">
        <v-col cols="12" class="d-flex justify-center">
            <div ref="panzoomContainer" class="panzoom-wrapper">
                <pdf
                    :data-cy="'DocumentPDF_' + documentId + '_pdf'"
                    :source="srcImport ? srcImport : src"
                    :page="page"
                    :rotation="rotation"
                    :resize="resize"
                    style="width: 100%; height: 100%"
                />
            </div>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import pdf from "vue-pdf-embed";
import gql from "graphql-tag";
import Panzoom, { PanzoomObject } from "@panzoom/panzoom";
import { Apollo } from "@/decorators";
import { Prop, Component, Vue, toNative } from "vue-facing-decorator";

@Component({
    components: {
        pdf,
    },
})
export class DocumentPDF extends Vue {
    /**
     * @prop {string} documentId - The ID of the document. Default is an empty string.
     */
    @Prop({ default: "" }) documentId!: string;
    /**
     * @prop {string} srcImport - The source import path for the document. Default is an empty string.
     */
    @Prop({ default: "" }) srcImport!: string;
    /**
     * @prop {number} page - The page number to display. Default is 1.
     */
    @Prop() page: number;
    /**
     * @prop {number} rotation - The rotation angle for the document. Default is 0.
     */
    @Prop({ default: 0 }) rotation!: number;
    /**
     * @prop {boolean} resize - Flag to determine if the document should be resized. Default is true.
     */
    @Prop({ default: true }) resize!: boolean;
    /**
     * @prop {boolean} scrollWheelZoomAvailable - Flag to determine if the document should be zoomed in or out using the scroll wheel. Default is false.
     */
    @Prop({ default: false }) scrollWheelZoomAvailable: boolean;

    numPages = 0;

    private panzoomInstance: PanzoomObject | null = null;
    private readonly maxScale = 5;
    private readonly minScale = 0.5;

    mounted() {
        const panzoomContainer = this.$refs.panzoomContainer as HTMLElement;
        if (panzoomContainer) {
            this.panzoomInstance = Panzoom(panzoomContainer, {
                maxScale: this.maxScale,
                minScale: this.minScale,
                contain: "outside",
            });
        }

        // Attach wheel event listener for zooming
        if (this.scrollWheelZoomAvailable) {
            panzoomContainer.addEventListener("wheel", this.handleWheel, {
                passive: false,
            });
        }
    }

    /**
     * Handles the wheel event to zoom in or out of the PDF
     */
    private handleWheel(event: WheelEvent) {
        event.preventDefault();

        if (!this.panzoomInstance) return;

        // To know if we zoom in and out, we get the deltaY of the scroll event, and we set a scaleDelta based on it (0.1 for zoom in, -0.1 for zoom out)
        const scaleDelta = event.deltaY > 0 ? -0.1 : 0.1;
        const currentScale = this.panzoomInstance.getScale();
        const newScale = currentScale + scaleDelta;

        //We restrain the scale of the zoom to be bound by our min and max scale
        const constrainedScale = Math.max(this.minScale, Math.min(newScale, this.maxScale));

        //We need to calculate the origin point of the zoom, so we get the container panzoom back, and get the rect of the container
        const panzoomContainer = this.$refs.panzoomContainer as HTMLElement;
        const rect = panzoomContainer.getBoundingClientRect();

        // Calculate the origin point of the zoom
        const originX = event.clientX - rect.left;
        const originY = event.clientY - rect.top;

        // Apply the zoom with the constrained scale
        this.panzoomInstance.zoom(constrainedScale, { originX, originY });
    }

    // Remove instance to avoid multiples listeners
    beforeUnmount() {
        if (this.panzoomInstance) {
            this.panzoomInstance.destroy();
            this.panzoomInstance = null;
        }
    }
    /**
     * Zooms in the PDF by increasing its zoom level.
     */
    public zoomIn(): void {
        if (this.panzoomInstance) {
            this.panzoomInstance.zoomIn();
        }
    }

    /**
     * Zooms out the PDF by decreasing its zoom level.
     */
    public zoomOut(): void {
        if (this.panzoomInstance) {
            this.panzoomInstance.zoomOut();
        }
    }

    /**
     * Resets the zoom level of the PDF to its default state.
     */
    public resetZoom(): void {
        if (this.panzoomInstance) {
            this.panzoomInstance.reset();
        }
    }

    @Apollo({
        query() {
            return gql`
                query getDocument($id: ID!) {
                    document(where: { id: $id }) {
                        id
                        originalSignedUrl
                        original {
                            id
                            key
                        }
                    }
                }
            `;
        },
        variables() {
            return {
                id: this.documentId,
            };
        },
        skip() {
            return !this.documentId;
        },
        specificResult({ data, loading }: any) {
            if (!loading) {
                if (data && data.document && data.document.originalSignedUrl) {
                    this.src = data.document.originalSignedUrl;
                    debugger;
                } else {
                    this.numPages = 0;
                }
            }
        },
    })
    src = "";
}

export default toNative(DocumentPDF);
</script>
<style>
.panzoom-wrapper {
    width: 100%;
    height: 100%;
    min-height: 600px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>
